import { useCallback, useEffect, useRef, useState } from 'react';

export type Notice = {
  message: string;
  kind: 'success' | 'info' | 'error';
  action?: { label: string; run: () => void };
};
export type NoticeInput = Notice | string;
export function useNotice() {
  const [notice, setNotice] = useState<NoticeInput | null>(null);
  const notify = useCallback(
    (next: NoticeInput) => setNotice(typeof next === 'string' ? { message: next, kind: 'info' } : { ...next }),
    [],
  );
  return { notice, notify };
}

export function Notification({ message }: { message: NoticeInput | null }) {
  const [dismissed, setDismissed] = useState(false);
  const [paused, setPaused] = useState(false);
  const remaining = useRef(4000);
  useEffect(() => {
    setDismissed(false);
    remaining.current = 4000;
  }, [message]);
  const kind = typeof message === 'string' ? 'info' : message?.kind;
  useEffect(() => {
    if (!message || paused || kind === 'error') return;
    const start = Date.now();
    const timer = setTimeout(() => setDismissed(true), remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (Date.now() - start));
    };
  }, [message, kind, paused]);
  if (!message || dismissed) return null;
  const text = typeof message === 'string' ? message : message.message;
  const action = typeof message === 'string' ? undefined : message.action;
  return (
    <div
      className={`sp-notification sp-notification-${kind}`}
      role={kind === 'error' ? 'alert' : 'status'}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
      }}
    >
      <span>{text}</span>
      {action && (
        <button className="sp-button" onClick={action.run}>
          {action.label}
        </button>
      )}
      <button className="sp-icon" aria-label="Dismiss notification" onClick={() => setDismissed(true)}>
        ×
      </button>
    </div>
  );
}
